import { create } from "zustand";
import { endpointPath } from "src/hooks/useFetch";

/**
 * Zustand store for Kate's "skills" — durable analysis playbooks that live in
 * the telemetryDB.KateSkill table.
 *
 * The store keeps two caches:
 *
 *   1. The lightweight `skills` index (name, title, description) — fetched
 *      once per app session via GET /api/ai/skills and injected into every
 *      system prompt so Kate knows what's available without paying for the
 *      full markdown content.
 *
 *   2. A per-name `contentCache` that lazily fills as Kate calls her
 *      `read_skill` tool. Once read, the content is reused for the rest of
 *      the session.
 *
 * Editing a skill in the DB does NOT automatically invalidate the caches.
 * The chat panel exposes a manual `refresh` button so the user can pick up
 * changes without a full page reload.
 */

export interface SkillSummary {
  name: string;
  title: string;
  description: string;
}

export interface Skill extends SkillSummary {
  content: string;
  updatedAt: string;
}

interface SkillListEnvelope {
  success: boolean;
  skills?: SkillSummary[];
  error?: string;
}

interface SkillEnvelope {
  success: boolean;
  skill?: Skill;
  error?: string;
}

interface SkillsState {
  skills: SkillSummary[];
  contentCache: Record<string, Skill>;
  isLoading: boolean;
  loadError: string | null;
  loadedAt: number | null;

  loadSkills: () => Promise<void>;
  refresh: () => Promise<void>;
  fetchSkillContent: (name: string) => Promise<Skill | { error: string }>;
}

const getProvider = ():
  | (() => Promise<string>)
  | undefined => window.__telemetryAuth?.getAccessTokenSilently;

const fetchAuthed = async (path: string): Promise<Response> => {
  const provider = getProvider();
  if (!provider) {
    throw new Error("Auth context unavailable");
  }
  const token = await provider();
  return fetch(endpointPath(path), {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const useSkillsStore = create<SkillsState>((set, get) => ({
  skills: [],
  contentCache: {},
  isLoading: false,
  loadError: null,
  loadedAt: null,

  loadSkills: async () => {
    if (get().isLoading) return;
    set({ isLoading: true, loadError: null });
    try {
      const res = await fetchAuthed("/ai/skills");
      if (!res.ok) {
        const text = await res.text().catch(() => res.statusText);
        throw new Error(`Skills endpoint ${res.status}: ${text || res.statusText}`);
      }
      const envelope = (await res.json()) as SkillListEnvelope;
      if (!envelope.success) {
        throw new Error(envelope.error || "Failed to load skills");
      }
      set({
        skills: envelope.skills ?? [],
        isLoading: false,
        loadedAt: Date.now(),
      });
    } catch (err) {
      set({
        isLoading: false,
        loadError: (err as Error).message,
      });
    }
  },

  refresh: async () => {
    set({ contentCache: {}, loadedAt: null });
    await get().loadSkills();
  },

  fetchSkillContent: async (name) => {
    const cached = get().contentCache[name];
    if (cached) return cached;

    try {
      const res = await fetchAuthed(`/ai/skills/${encodeURIComponent(name)}`);
      if (res.status === 404) {
        return { error: `Skill "${name}" not found` };
      }
      if (!res.ok) {
        const text = await res.text().catch(() => res.statusText);
        return {
          error: `Skill endpoint ${res.status}: ${text || res.statusText}`,
        };
      }
      const envelope = (await res.json()) as SkillEnvelope;
      if (!envelope.success || !envelope.skill) {
        return { error: envelope.error || "Failed to load skill" };
      }
      set((s) => ({
        contentCache: { ...s.contentCache, [name]: envelope.skill as Skill },
      }));
      return envelope.skill;
    } catch (err) {
      return { error: (err as Error).message };
    }
  },
}));
